import * as React from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'

export interface SidebarNavItem {
  id: string
  label: string
  icon?: React.ReactNode
  href?: string
  onClick?: () => void
  badge?: React.ReactNode
  children?: SidebarNavItem[]
}

export interface SidebarNavProps {
  items: SidebarNavItem[]
  activeId?: string
  className?: string
  onItemClick?: (item: SidebarNavItem) => void
  isCollapsed?: boolean
}

function SidebarNav({
  items,
  activeId,
  className,
  onItemClick,
  isCollapsed,
}: SidebarNavProps) {
  const [expanded, setExpanded] = React.useState<Set<string>>(() => {
    const initial = new Set<string>()
    for (const item of items) {
      if (item.children?.some(child => child.id === activeId)) {
        initial.add(item.id)
      }
    }
    return initial
  })

  React.useEffect(() => {
    setExpanded(prev => {
      const next = new Set(prev)
      let changed = false
      for (const item of items) {
        if (
          item.children?.some(child => child.id === activeId) &&
          !next.has(item.id)
        ) {
          next.add(item.id)
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [activeId, items])

  const toggleExpanded = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <nav className={cn('flex flex-col gap-1 p-4', className)}>
      {items.map(item => {
        const hasChildren = item.children && item.children.length > 0
        const isExpanded = expanded.has(item.id)
        const hasActiveChild = item.children?.some(
          child => child.id === activeId
        )
        const isActive = item.id === activeId

        if (hasChildren) {
          return (
            <div key={item.id}>
              <button
                type="button"
                title={isCollapsed ? item.label : undefined}
                onClick={() => {
                  if (isCollapsed) {
                    const firstChild = item.children![0]
                    if (firstChild) {
                      onItemClick?.(firstChild)
                    }
                  } else {
                    toggleExpanded(item.id)
                  }
                }}
                className={cn(
                  'flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors cursor-pointer',
                  'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  hasActiveChild
                    ? 'text-sidebar-primary'
                    : 'text-sidebar-foreground',
                  isCollapsed && 'justify-center px-2'
                )}
              >
                {item.icon && (
                  <span
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center',
                      hasActiveChild
                        ? 'text-sidebar-primary'
                        : 'text-muted-foreground'
                    )}
                  >
                    {item.icon}
                  </span>
                )}
                {!isCollapsed && (
                  <>
                    <span className="flex-1 whitespace-nowrap text-left">
                      {item.label}
                    </span>
                    <ChevronRight
                      className={cn(
                        'h-4 w-4 shrink-0 transition-transform duration-200',
                        isExpanded && 'rotate-90'
                      )}
                    />
                  </>
                )}
              </button>
              {!isCollapsed && (
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-200',
                    isExpanded ? 'max-h-40' : 'max-h-0'
                  )}
                >
                  <div className="flex flex-col gap-0.5 pt-0.5">
                    {item.children!.map(child => {
                      const isChildActive = child.id === activeId
                      return (
                        <button
                          key={child.id}
                          type="button"
                          onClick={() => {
                            child.onClick?.()
                            onItemClick?.(child)
                          }}
                          className={cn(
                            'rounded-md py-2 pl-10 pr-3 text-left text-sm font-medium transition-colors cursor-pointer',
                            isChildActive
                              ? 'bg-accent text-sidebar-primary'
                              : 'text-[#4a5565] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                          )}
                        >
                          {child.label}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )
        }

        return (
          <SidebarNavButton
            key={item.id}
            item={item}
            isActive={isActive}
            isCollapsed={isCollapsed}
            onClick={() => {
              item.onClick?.()
              onItemClick?.(item)
            }}
          />
        )
      })}
    </nav>
  )
}

interface SidebarNavButtonProps {
  item: SidebarNavItem
  isActive: boolean
  onClick: () => void
  isCollapsed?: boolean
}

function SidebarNavButton({
  item,
  isActive,
  onClick,
  isCollapsed,
}: SidebarNavButtonProps) {
  const Component = item.href ? 'a' : 'button'

  return (
    <Component
      href={item.href}
      onClick={(e: React.MouseEvent) => {
        if (item.href) e.preventDefault()
        onClick()
      }}
      title={isCollapsed ? item.label : undefined}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors cursor-pointer',
        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive
          ? 'bg-sidebar-accent text-sidebar-primary'
          : 'text-sidebar-foreground',
        isCollapsed && 'justify-center px-2'
      )}
    >
      {item.icon && (
        <span
          className={cn(
            'flex h-5 w-5 shrink-0 items-center justify-center',
            isActive ? 'text-sidebar-primary' : 'text-muted-foreground'
          )}
        >
          {item.icon}
        </span>
      )}
      {!isCollapsed && (
        <>
          <span className="flex-1 whitespace-nowrap">{item.label}</span>
          {item.badge && <span>{item.badge}</span>}
        </>
      )}
    </Component>
  )
}

export interface SidebarProps {
  children: React.ReactNode
  className?: string
  isCollapsed?: boolean
}

function Sidebar({ children, className, isCollapsed }: SidebarProps) {
  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-sidebar-border bg-sidebar-background transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-16' : 'w-56',
        className
      )}
    >
      {children}
    </aside>
  )
}

function SidebarContent({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return <div className={cn('flex-1 overflow-auto', className)}>{children}</div>
}

export { Sidebar, SidebarContent, SidebarNav }
